// This file contains components for navigation blocking
import { NavigationBlockerContext } from '@hooks/useNavigationBlockerContext'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
  UNSAFE_NavigationContext,
  useLocation,
  useNavigate,
} from 'react-router-dom'

function useNavigationBlocker() {
  const blockerFunctionRef = useRef<(path: string) => boolean>(() => true)
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null,
  )
  const navigationContext = useContext(UNSAFE_NavigationContext)
  const location = useLocation()
  const navigate = useNavigate()
  const lastLocationRef = useRef(location)

  const blockNavigation = useCallback((callback: (path: string) => boolean) => {
    blockerFunctionRef.current = callback
  }, [])

  const showNavigationDialog = useCallback((path: string) => {
    setPendingNavigation(path)
  }, [])

  const confirmNavigation = useCallback(() => {
    if (pendingNavigation) {
      const targetPath = pendingNavigation
      setPendingNavigation(null)
      setTimeout(() => navigate(targetPath), 0)
    }
  }, [pendingNavigation, navigate])

  const cancelNavigation = useCallback(() => {
    setPendingNavigation(null)
  }, [])

  const shouldBlockNavigation = useCallback((path: string) => {
    return !blockerFunctionRef.current(path)
  }, [])

  // Handle navigation method overrides
  useEffect(() => {
    const navigator = navigationContext.navigator
    const originalPush = navigator.push
    const originalReplace = navigator.replace
    const originalGo = navigator.go

    navigator.push = (to: string, state?: unknown) => {
      if (blockerFunctionRef.current(to)) {
        originalPush(to, state)
      } else {
        setPendingNavigation(to)
      }
    }

    navigator.replace = (to: string, state?: unknown) => {
      if (blockerFunctionRef.current(to)) {
        originalReplace(to, state)
      } else {
        setPendingNavigation(to)
      }
    }

    navigator.go = (delta: number) => {
      const targetPath = location.pathname
      if (blockerFunctionRef.current(targetPath)) {
        originalGo(delta)
      } else {
        setPendingNavigation(targetPath)
      }
    }

    return () => {
      navigator.push = originalPush
      navigator.replace = originalReplace
      navigator.go = originalGo
    }
  }, [navigationContext, location.pathname])

  // Handle link clicks
  useEffect(() => {
    function handleClick(event: MouseEvent) {
      const anchor = (event.target as HTMLElement).closest('a')
      if (!anchor) return

      const href = anchor.getAttribute('href')
      if (href && href.startsWith('/')) {
        if (!blockerFunctionRef.current(href)) {
          event.preventDefault()
          event.stopPropagation()
          setPendingNavigation(href)
        }
      }
    }

    document.addEventListener('click', handleClick, { capture: true })
    return () =>
      document.removeEventListener('click', handleClick, { capture: true })
  }, [])

  // Handle location changes
  useEffect(() => {
    if (location !== lastLocationRef.current) {
      const shouldProceed = blockerFunctionRef.current(location.pathname)
      if (!shouldProceed) {
        setPendingNavigation(location.pathname)
        return
      }
      lastLocationRef.current = location
    }
  }, [location])

  return {
    blockNavigation,
    showNavigationDialog,
    shouldBlockNavigation,
    pendingNavigation,
    confirmNavigation,
    cancelNavigation,
  }
}

export function NavigationBlockerProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const navigationBlocker = useNavigationBlocker()

  return (
    <NavigationBlockerContext.Provider value={navigationBlocker}>
      {children}
    </NavigationBlockerContext.Provider>
  )
}
